import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowRight, Calculator, Truck, CheckCircle } from "lucide-react";

const POUR_IMG = "https://images.pexels.com/photos/2469/building-construction-building-site-constructing.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const concreteGrades = [
  { grade: "C8/10 (B150)", description: "Beton de egalizare, umpluturi", price: 360, applications: ["Egalizări", "Umpluturi", "Borduri"] },
  { grade: "C12/15 (B200)", description: "Fundații case, zidării", price: 385, applications: ["Fundații case", "Zidării", "Garduri"] },
  { grade: "C16/20 (B250)", description: "Plăci, trotuare, platforme", price: 405, applications: ["Plăci beton", "Trotuare", "Platforme auto"] },
  { grade: "C18/22.5 (B300)", description: "Structuri ușoare", price: 425, applications: ["Structuri ușoare", "Pereți", "Scări"] },
  { grade: "C20/25 (B350)", description: "Structuri rezidențiale", price: 445, applications: ["Stâlpi", "Grinzi", "Planșee rezidențiale"] },
  { grade: "C25/30 (B400)", description: "Structuri industriale", price: 460, applications: ["Hale industriale", "Parcări etajate", "Structuri comerciale"] },
  { grade: "C30/37 (B450)", description: "Construcții speciale", price: 485, applications: ["Poduri", "Rezervoare", "Structuri prefabricate"] },
  { grade: "C32/40 (B500)", description: "Infrastructură", price: 520, applications: ["Infrastructură rutieră", "Fundații speciale", "Construcții înalte"] },
  { grade: "C35/45 (B550)", description: "Proiecte complexe", price: 535, applications: ["Proiecte speciale", "Clădiri înalte", "Infrastructură majoră"] },
  { grade: "C40/50 (B600)", description: "Construcții grele", price: 725, applications: ["Construcții grele", "Proiecte industriale", "Infrastructură critică"] },
  { grade: "Șapă M100", description: "Șapă autonivelantă", price: 440, applications: ["Pardoseli", "Nivelare", "Finisaje"] },
];

const additionalServices = [
  { name: "Transport beton", price: "10 RON/km (x 2 dus+intors)" },
  { name: "Pompare beton", price: "100-150 RON/m³" },
  { name: "Aditivi speciali", price: "La cerere" },
];

const aggregates = [
  { name: "Nisip Natural 0-4", granulation: "54,45 RON", description: "Nisip pentru preparare betoane și mortare", unit: "tonă" },
  { name: "Pietriș 4-8", granulation: "45.98 RON", description: "Pietriș sortat pentru betoane și drenaje", unit: "tonă" },
  { name: "Pietriș 8-16", granulation: "38.72 RON", description: "Pietriș sortat pentru betoane și fundații", unit: "tonă" },
  { name: "Balast", granulation: "38.72 RON ", description: "Balast natural pentru fundații și umpluturi", unit: "tonă" },
  { name: "Nisip de concasare", granulation: "60.00 RON", description: "Nisip din piatră concasată", unit: "tonă" },
  { name: "Piatră spartă", granulation: "16-31 mm", description: "Piatră concasată pentru drumuri și fundații", unit: "tonă" },
];

export const Services = () => {
  const [selectedGrade, setSelectedGrade] = useState("");
  const [volume, setVolume] = useState("");
  const [distance, setDistance] = useState("");
  const [calculatedPrice, setCalculatedPrice] = useState(null);

  const TRANSPORT_PRICE_PER_KM = 10;

  const calculateTotal = (grade, vol, dist) => {
    if (!grade || !vol) return null;
    const gradeData = concreteGrades.find(g => g.grade === grade);
    if (!gradeData) return null;
    
    const volumeNum = parseFloat(vol);
    const distanceNum = parseFloat(dist) || 0;
    
    if (volumeNum <= 0) return null;
    
    const concreteTotal = gradeData.price * volumeNum;
    const transportTotal = distanceNum * TRANSPORT_PRICE_PER_KM;
    
    return {
      unitPrice: gradeData.price,
      volume: volumeNum,
      distance: distanceNum,
      concreteTotal,
      transportTotal,
      total: concreteTotal + transportTotal,
    };
  };

  const handleVolumeChange = (e) => {
    const val = e.target.value;
    setVolume(val);
    setCalculatedPrice(calculateTotal(selectedGrade, val, distance));
  };

  const handleGradeChange = (val) => {
    setSelectedGrade(val);
    setCalculatedPrice(calculateTotal(val, volume, distance));
  };

  const handleDistanceChange = (e) => {
    const val = e.target.value;
    setDistance(val);
    setCalculatedPrice(calculateTotal(selectedGrade, volume, val));
  };

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#FAFAFA] border-b border-[#D4D4D4]" data-testid="services-hero">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
              Servicii
            </p>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter uppercase mb-6">
              BETON READYMIX<br />PENTRU ORICE PROIECT
            </h1>
            <p className="text-lg text-[#737373] max-w-2xl font-medium leading-relaxed">
              Oferim o gamă completă de clase de beton, de la C8/10 pentru lucrări simple până la C35/45 pentru proiecte de infrastructură.
            </p>
          </div>
        </div>
      </section>

      {/* Price Calculator */}
      <section className="py-24 md:py-32 bg-white" data-testid="calculator-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
              Calculator Preț
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              ESTIMEAZĂ COSTUL PROIECTULUI
            </h2>
            <p className="text-[#737373]">
              Selectează clasa de beton și cantitatea necesară pentru a obține o estimare de preț.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Calculator Form */}
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="grade" className="text-xs font-bold tracking-[0.1em] uppercase">
                  Clasă Beton
                </Label>
                <Select value={selectedGrade} onValueChange={handleGradeChange}>
                  <SelectTrigger 
                    className="h-14 rounded-none border-2 border-[#D4D4D4] focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]"
                    data-testid="calc-grade-select"
                  >
                    <SelectValue placeholder="Selectează clasa de beton" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {concreteGrades.map((grade) => (
                      <SelectItem 
                        key={grade.grade} 
                        value={grade.grade}
                        className="rounded-none"
                        data-testid={`grade-option-${grade.grade}`}
                      >
                        {grade.grade} - {grade.description} ({grade.price} RON/m³)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="volume" className="text-xs font-bold tracking-[0.1em] uppercase">
                  Volum (m³)
                </Label>
                <Input
                  id="volume"
                  type="number"
                  min="0.5"
                  step="0.5"
                  placeholder="Ex: 10"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="h-14 rounded-none border-2 border-[#D4D4D4] focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]"
                  data-testid="calc-volume-input"
                />
                <p className="text-xs text-[#737373]">Cantitate minimă: 0.5 m³</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="distance" className="text-xs font-bold tracking-[0.1em] uppercase">
                  Distanță Transport (km)
                </Label>
                <Input
                  id="distance"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Ex: 15"
                  value={distance}
                  onChange={handleDistanceChange}
                  className="h-14 rounded-none border-2 border-[#D4D4D4] focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]"
                  data-testid="calc-distance-input"
                />
                <p className="text-xs text-[#737373]">Preț transport: 10 RON/km (doar dus)</p>
              </div>
            </div>

            {/* Result Box */}
            <div className="lg:col-span-5">
              <div className="bg-[#0A0A0A] p-8 h-full min-h-[320px] flex flex-col justify-center" data-testid="calc-result-box">
                {calculatedPrice ? (
                  <>
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#737373] mb-2">
                      Preț Total Estimat
                    </p>
                    <p className="text-5xl md:text-6xl font-black text-[#FACC15] mb-4">
                      {calculatedPrice.total.toLocaleString()} RON
                    </p>
                    <div className="space-y-2 text-white/60 text-sm">
                      <p>Clasă: <span className="text-white">{selectedGrade}</span></p>
                      <p>Preț beton: <span className="text-white">{calculatedPrice.unitPrice} RON/m³ × {calculatedPrice.volume} m³ = {calculatedPrice.concreteTotal.toLocaleString()} RON</span></p>
                      {calculatedPrice.distance > 0 && (
                        <p>Transport: <span className="text-white">10 RON/km × {calculatedPrice.distance} km = {calculatedPrice.transportTotal.toLocaleString()} RON</span></p>
                      )}
                    </div>
                    <p className="text-xs text-[#737373] mt-6">
                      * Preț estimativ. Contactați-ne pentru ofertă personalizată.
                    </p>
                  </>
                ) : (
                  <div className="text-center">
                    <Calculator className="w-12 h-12 text-[#737373] mx-auto mb-4" />
                    <p className="text-white font-medium mb-2">Completează formularul</p>
                    <p className="text-[#737373] text-sm">
                      Selectează clasa de beton, introdu cantitatea și distanța pentru a vedea prețul estimat.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Concrete Grades Table */}
      <section className="py-24 md:py-32 bg-[#FAFAFA] border-t border-b border-[#D4D4D4]" data-testid="grades-table-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
              Clasele Noastre de Beton
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              SPECIFICAȚII TEHNICE
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border border-[#D4D4D4]" data-testid="grades-table">
              <thead>
                <tr className="bg-[#0A0A0A] text-white">
                  <th className="p-4 text-left font-bold text-xs tracking-[0.1em] uppercase">Clasă</th>
                  <th className="p-4 text-left font-bold text-xs tracking-[0.1em] uppercase border-l border-[#333]">Descriere</th>
                  <th className="p-4 text-left font-bold text-xs tracking-[0.1em] uppercase border-l border-[#333]">Aplicații</th>
                  <th className="p-4 text-right font-bold text-xs tracking-[0.1em] uppercase border-l border-[#333]">Preț/m³</th>
                </tr>
              </thead>
              <tbody>
                {concreteGrades.map((grade, index) => (
                  <tr 
                    key={grade.grade} 
                    className={`border-t border-[#D4D4D4] ${index % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'} hover:bg-[#FEF9C3] transition-colors`}
                    data-testid={`table-row-${grade.grade}`}
                  >
                    <td className="p-4 font-bold text-lg">{grade.grade}</td>
                    <td className="p-4 text-[#737373] border-l border-[#D4D4D4]">{grade.description}</td>
                    <td className="p-4 border-l border-[#D4D4D4]">
                      <ul className="space-y-1">
                        {grade.applications.map((app, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-[#737373]">
                            <CheckCircle className="w-3 h-3 text-[#FACC15]" />
                            {app}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="p-4 text-right font-bold text-xl text-[#0A0A0A] border-l border-[#D4D4D4]">
                      {grade.price} <span className="text-sm font-normal text-[#737373]">RON</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Aggregates Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="aggregates-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
              Agregate și Balast
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              MATERIALE PENTRU CONSTRUCȚII
            </h2>
            <p className="text-[#737373] mt-4">
              Pe lângă beton, oferim o gamă completă de agregate de calitate pentru toate tipurile de lucrări de construcții.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aggregates.map((item, index) => (
              <div 
                key={index}
                className="border border-[#D4D4D4] p-6 hover:border-[#FACC15] transition-colors"
                data-testid={`aggregate-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold">{item.name}</h3>
                  <span className="text-xs font-bold tracking-[0.1em] uppercase bg-[#FACC15] text-[#0A0A0A] px-3 py-1">
                    {item.granulation}
                  </span>
                </div>
                <p className="text-[#737373] text-sm mb-4">{item.description}</p>
                <div className="flex items-center justify-between pt-4 border-t border-[#D4D4D4]">
                  <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#737373]">
                    Unitate de măsură
                  </span>
                  <span className="font-bold">{item.unit}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 bg-[#FAFAFA] border border-[#D4D4D4]">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="font-bold text-lg mb-1">Solicitați o ofertă personalizată pentru agregate</p>
                <p className="text-[#737373] text-sm">Prețurile variază în funcție de cantitate și distanța de livrare.</p>
              </div>
              <a 
                href="tel:+40722344534"
                className="inline-flex items-center justify-center bg-[#FACC15] text-[#0A0A0A] hover:bg-yellow-400 rounded-none font-bold tracking-wider uppercase text-xs px-6 py-4 transition-colors"
                data-testid="aggregates-call-btn"
              >
                Sună pentru Ofertă
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 md:py-32 bg-white" data-testid="additional-services-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
                Servicii Adiționale
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">
                SOLUȚII COMPLETE
              </h2>
              <p className="text-base font-medium leading-relaxed text-[#737373] mb-8">
                Pe lângă betonul readymix de calitate, oferim și servicii complementare pentru a vă asigura că proiectul dumneavoastră se desfășoară fără probleme.
              </p>
              <div className="space-y-4">
                {additionalServices.map((service, index) => (
                  <div 
                    key={index} 
                    className="flex justify-between items-center p-4 border border-[#D4D4D4] bg-[#FAFAFA]"
                    data-testid={`additional-service-${index}`}
                  >
                    <span className="font-medium">{service.name}</span>
                    <span className="text-[#FACC15] font-bold">{service.price}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7">
              <img 
                src={POUR_IMG} 
                alt="Turnare beton" 
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Delivery Info */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="delivery-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Truck className="w-16 h-16 text-[#FACC15] mb-6" />
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
                Livrare
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6 text-white">
                LIVRARE RAPIDĂ ȘI SIGURĂ
              </h2>
              <p className="text-base font-medium leading-relaxed text-[#737373] mb-6">
                Dispunem de o flotă modernă de autobetoniere care asigură livrarea la timp a betonului proaspăt pe șantierul dumneavoastră.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-[#FACC15]" />
                  Transport: 10 RON/km (doar dus)
                </li>
                <li className="flex items-center gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-[#FACC15]" />
                  Livrare în toată zona Focșani și împrejurimi
                </li>
                <li className="flex items-center gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-[#FACC15]" />
                  Program flexibil, de luni până vineri
                </li>
                <li className="flex items-center gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-[#FACC15]" />
                  Comandă telefonică rapidă
                </li>
              </ul>
            </div>
            <div className="text-center lg:text-right">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#737373] mb-2">
                Comandă Acum
              </p>
              <a 
                href="tel:+40722344534" 
                className="text-4xl md:text-5xl font-black text-[#FACC15] hover:text-yellow-400 transition-colors"
                data-testid="delivery-phone-link"
              >
                +40 722 344 534
              </a>
              <p className="text-[#737373] mt-4">
                Luni - Vineri: 07:30 - 16:30
              </p>
              <Button 
                asChild
                variant="outline"
                className="mt-8 bg-transparent border-2 border-[#FACC15] text-[#FACC15] hover:bg-[#FACC15] hover:text-[#0A0A0A] rounded-none font-bold tracking-wider uppercase text-sm px-8 py-6"
                data-testid="delivery-contact-btn"
              >
                <Link to="/contact">
                  Vezi Locația
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
